// Web-exclusive Inbox screen - Notifications Page
// Displays user notifications with read/unread filtering

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { AlertTriangle, Bell, Check } from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import { webColors, webSpacing, webBorderRadius } from '../../constants/webStyles';
import { getNotifications, readNotifications } from '../../services/profile';
import { getApplication } from '../../services/job';
import { useIsMobile } from '../../hooks/useIsMobile';

const InboxScreen = () => {
  const navigation = useNavigation();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const { slug } = useSelector((state) => state.user);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: notificationsData, isLoading, error } = useQuery({
    queryKey: ['notifications', slug],
    queryFn: () => getNotifications({ uuid: slug, page: 1 }),
    enabled: !!slug,
  });
  const notifications = notificationsData?.data?.data || [];

  // Mark notification as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notifId) => readNotifications({ uuid: slug, data: { id: notifId } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', slug] }),
  });

  const handleMarkAsRead = (notifId) => {
    markAsReadMutation.mutate(notifId);
  };

  // Mirrors the native Inbox.js press handler: notification links are either
  // `api/jobs/{job_uuid}` or `api/applications/{application_uuid}`. Job links
  // carry a job uuid (DetailsScreen fetches it directly); application links
  // carry an application uuid that must be resolved to its job first.
  const handleNotificationPress = async (notif) => {
    if (notif.read === 0) {
      handleMarkAsRead(notif.id);
    }

    const segments = notif?.link?.split('/') || [];
    const type = segments[1]; // "jobs" | "applications" | "profile"
    const uuid = segments[2]; // job uuid OR application uuid

    if (type === 'jobs' && uuid) {
      navigation.navigate('DetailsScreen', { jobId: uuid });
    } else if (type === 'applications' && uuid) {
      try {
        const res = await queryClient.fetchQuery({
          queryKey: ['jobApplication', { query: uuid }],
          queryFn: getApplication,
        });
        const appData = res?.data?.data;
        const job = appData?.job;
        if (job?.uuid) {
          navigation.navigate('DetailsScreen', {
            jobId: job.uuid,
            data: {
              ...job,
              status: appData?.status_code,
              job_application: { uuid: appData?.uuid },
            },
          });
        }
      } catch (e) {
        console.warn('Failed to open application notification', e);
      }
    }
    // api/profile and unknown links: no navigation (parity with native)
  };

  // Filter notifications by read status
  const filteredNotifications = notifications.filter((notif) => {
    if (selectedStatus === 'all') return true;
    if (selectedStatus === 'unread') return notif.read === 0;
    if (selectedStatus === 'read') return notif.read === 1;
    return true;
  });

  const statusTabs = [
    { id: 'all', label: 'All', count: notifications.length },
    { id: 'unread', label: 'Unread', count: notifications.filter(n => n.read === 0).length },
    { id: 'read', label: 'Read', count: notifications.filter(n => n.read === 1).length },
  ];

  return (
    <WebLayout>
      <ScrollView style={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        <View style={styles.pageHeader}>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>Notifications</Text>
          <Text style={styles.pageSubtitle}>
            Stay updated with your latest notifications
          </Text>
        </View>

        {/* Status Filter Tabs */}
        <View style={styles.tabsContainer}>
          {statusTabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tab, selectedStatus === tab.id && styles.tabActive]}
              onPress={() => setSelectedStatus(tab.id)}
            >
              <Text style={[styles.tabText, selectedStatus === tab.id && styles.tabTextActive]}>
                {tab.label}
              </Text>
              {tab.count > 0 && (
                <View style={[styles.tabBadge, selectedStatus === tab.id && styles.tabBadgeActive]}>
                  <Text style={[styles.tabBadgeText, selectedStatus === tab.id && styles.tabBadgeTextActive]}>
                    {tab.count}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </View>

        {/* Content */}
        <View style={styles.contentContainer}>
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={webColors.primary} />
              <Text style={styles.loadingText}>Loading notifications...</Text>
            </View>
          ) : error ? (
            <View style={styles.errorState}>
              <View style={styles.errorIcon}>
                <AlertTriangle size={48} color="#92400e" />
              </View>
              <Text style={styles.errorTitle}>Unable to load notifications</Text>
              <Text style={styles.errorText}>Please check your connection and try again</Text>
            </View>
          ) : filteredNotifications.length > 0 ? (
            <NotificationsList
              notifications={filteredNotifications}
              onPressNotification={handleNotificationPress}
            />
          ) : (
            <EmptyState
              status={selectedStatus}
              navigation={navigation}
              onClearFilter={() => setSelectedStatus('all')}
            />
          )}
        </View>
      </ScrollView>
    </WebLayout>
  );
};

// Notifications List Component
const NotificationsList = ({ notifications, onPressNotification }) => {
  return (
    <View style={styles.listContainer}>
      {notifications.map((notif, index) => (
        <TouchableOpacity
          key={notif.id || index}
          style={[styles.inboxItem, notif.read === 0 && styles.inboxItemUnread]}
          onPress={() => onPressNotification(notif)}
        >
          {notif.read === 0 && <View style={styles.unreadIndicator} />}
          <View style={[styles.inboxIcon, { backgroundColor: getIconBg(notif.read) }]}>
            {notif.read === 0 ? (
              <Bell size={20} color="#1e40af" />
            ) : (
              <Check size={20} color="#6b7280" />
            )}
          </View>
          <View style={styles.inboxContent}>
            <View style={styles.inboxHeader}>
              <Text style={styles.inboxTitle} numberOfLines={1}>{notif.title}</Text>
              <Text style={styles.inboxTime}>{formatDate(notif.created_at)}</Text>
            </View>
            <Text style={styles.inboxPreview} numberOfLines={2}>
              {notif.body}
            </Text>
            {notif.note && (
              <Text style={styles.inboxNote} numberOfLines={1}>
                {notif.note}
              </Text>
            )}
            <View style={styles.inboxFooter}>
              <View style={[styles.statusPill, getStatusStyle(notif.read)]}>
                <Text style={[styles.statusPillText, getStatusTextStyle(notif.read)]}>
                  {formatStatus(notif.read)}
                </Text>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );
};

// Empty State Component
const EmptyState = ({ status, navigation, onClearFilter }) => {
  const getMessage = () => {
    switch (status) {
      case 'unread':
        return 'No unread notifications';
      case 'read':
        return 'No read notifications';
      default:
        return 'No notifications yet';
    }
  };

  return (
    <View style={styles.emptyState}>
      <View style={styles.emptyIcon}>
        <Bell size={48} color={webColors.textMuted} />
      </View>
      <Text style={styles.emptyTitle}>{getMessage()}</Text>
      <Text style={styles.emptyText}>
        {status === 'all'
          ? 'You will receive notifications about your jobs and applications here'
          : 'Notifications with this status will appear here'}
      </Text>
      <View style={styles.emptyActions}>
        {status === 'all' ? (
          <TouchableOpacity
            style={styles.emptyBtn}
            onPress={() => navigation.navigate('Jobs')}
          >
            <Text style={styles.emptyBtnText}>Browse Jobs</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={styles.emptyBtnSecondary}
            onPress={onClearFilter}
          >
            <Text style={styles.emptyBtnSecondaryText}>Show All Notifications</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

// Helper functions
const getIconBg = (read) => {
  // read: 0 = unread, 1 = read
  return read === 0 ? '#dbeafe' : '#f3f4f6';
};

const formatStatus = (read) => {
  return read === 0 ? 'Unread' : 'Read';
};

const getStatusStyle = (read) => {
  return read === 0
    ? { backgroundColor: '#dbeafe' }
    : { backgroundColor: '#f3f4f6' };
};

const getStatusTextStyle = (read) => {
  return read === 0
    ? { color: '#1e40af' }
    : { color: '#6b7280' };
};

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-MY', { month: 'short', day: 'numeric' });
};

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.medium,
    padding: 4,
    marginBottom: 24,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: webColors.border,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: webBorderRadius.small,
  },
  tabActive: {
    backgroundColor: webColors.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textMuted,
  },
  tabTextActive: {
    color: '#ffffff',
  },
  tabBadge: {
    minWidth: 20,
    height: 20,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  tabBadgeActive: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  tabBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: webColors.textSecondary,
  },
  tabBadgeTextActive: {
    color: '#ffffff',
  },
  contentContainer: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    borderWidth: 1,
    borderColor: webColors.border,
    overflow: 'hidden',
    minHeight: 300,
  },
  loadingContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: webColors.textMuted,
  },
  errorState: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  errorIcon: {
    marginBottom: 16,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  listContainer: {},
  inboxItem: {
    flexDirection: 'row',
    gap: 16,
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
    position: 'relative',
  },
  inboxItemUnread: {
    backgroundColor: '#f0f7ff',
  },
  unreadIndicator: {
    position: 'absolute',
    left: 8,
    top: '50%',
    marginTop: -4,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: webColors.primary,
  },
  inboxIcon: {
    width: 48,
    height: 48,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inboxContent: {
    flex: 1,
  },
  inboxHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  inboxTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  inboxTime: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  inboxSubject: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textLink,
    marginBottom: 4,
  },
  inboxPreview: {
    fontSize: 14,
    color: webColors.textMuted,
    lineHeight: 20,
    marginBottom: 8,
  },
  inboxNote: {
    fontSize: 13,
    color: webColors.textSecondary,
    fontStyle: 'italic',
    marginBottom: 12,
  },
  inboxFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: webBorderRadius.small,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '600',
  },
  viewDetailsLink: {},
  viewDetailsLinkText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.primary,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  emptyIcon: {
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 24,
    textAlign: 'center',
  },
  emptyActions: {
    flexDirection: 'row',
    gap: 12,
  },
  emptyBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
  },
  emptyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  emptyBtnSecondary: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
  },
  emptyBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
});

export default InboxScreen;
